/**
 * Attach a file to a Workshop thread. On the desk the paperclip opens the file
 * picker; on a phone it opens a sheet (Photo or video, File) floating 8px in,
 * with the pegs. Pictures join the project's Media and every other file its
 * files, so every thread can use them (WorkshopFile, ViewProjectImages).
 *
 * "From the brand library" is the media picker pane, stage 2b; not offered here.
 */
import { useRef, useState, type DragEvent } from "react";
import { FileText, FileUp, ImageIcon, Paperclip, Plus } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";

/** What the drop target and the sheet list, in the order the frames name them. */
export const WORKSHOP_ATTACH_TYPES = "PDF · VIDEO · IMAGE · CSV · XLSX · TXT · MD · JSON";

/** Where an attached file goes: true of pictures and files alike. */
export const WORKSHOP_ATTACH_WHERE = "It joins this project, so every chat can use it.";

type Props = {
  onAttachFiles: (files: File[]) => void;
  disabled: boolean;
  isPhone: boolean;
  /** Everything the paperclip takes. */
  accept: string;
  /** Phone: pictures and videos. */
  photoAccept: string;
  /** Phone: documents, sheets, text and videos. */
  fileAccept: string;
};

function Picker({
  inputRef,
  accept,
  onFiles,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  accept: string;
  onFiles: (files: File[]) => void;
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      multiple
      hidden
      onChange={(e) => {
        // Snapshot first: a live FileList empties once the value is reset.
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = "";
        if (files.length) onFiles(files);
      }}
    />
  );
}

export default function WorkshopAttach({
  onAttachFiles,
  disabled,
  isPhone,
  accept,
  photoAccept,
  fileAccept,
}: Props) {
  const allRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = useState(false);

  const choose = (ref: React.RefObject<HTMLInputElement | null>) => {
    setSheet(false);
    ref.current?.click();
  };

  return (
    <>
      <button
        type="button"
        className="m7r-attach ws-attach"
        disabled={disabled}
        aria-label="Attach a file"
        title="Attach a file"
        aria-haspopup={isPhone ? "dialog" : undefined}
        onClick={() => (isPhone ? setSheet(true) : allRef.current?.click())}
      >
        {isPhone ? <Paperclip aria-hidden strokeWidth={1.75} /> : <Plus aria-hidden strokeWidth={1.75} />}
      </button>
      <Picker inputRef={allRef} accept={accept} onFiles={onAttachFiles} />
      <Picker inputRef={photoRef} accept={photoAccept} onFiles={onAttachFiles} />
      <Picker inputRef={fileRef} accept={fileAccept} onFiles={onAttachFiles} />
      {isPhone ? (
        <KitModal
          isOpen={sheet}
          onOpenChange={setSheet}
          aria-label="Attach"
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet ws-attach-sheet ws-thread-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <div className="ws-action-sheet-head">
            <p className="ws-sheet-title">Attach</p>
            <p className="ws-action-sheet-meta">{WORKSHOP_ATTACH_TYPES}</p>
          </div>
          <div className="ws-action-sheet-list ws-attach-list">
            <button type="button" className="ws-action-sheet-item" onClick={() => choose(photoRef)}>
              <ImageIcon className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
              Photo or video
            </button>
            <button type="button" className="ws-action-sheet-item" onClick={() => choose(fileRef)}>
              <FileText className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
              File
            </button>
          </div>
          <p className="ws-attach-note">{WORKSHOP_ATTACH_WHERE}</p>
        </KitModal>
      ) : null}
    </>
  );
}

/**
 * The whole thread takes a dropped file, not just the paperclip. dragenter and
 * dragleave fire for every child crossed; a depth count keeps the target from
 * flickering off while the file moves across the thread.
 */
export function useThreadDrop(onDrop: (files: File[]) => void, disabled: boolean) {
  const depth = useRef(0);
  const [active, setActive] = useState(false);
  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");
  return {
    active: active && !disabled,
    handlers: {
      onDragEnter: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setActive(true);
      },
      onDragOver: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      },
      onDragLeave: (e: DragEvent) => {
        if (disabled || !hasFiles(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setActive(false);
      },
      onDrop: (e: DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setActive(false);
        if (disabled) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onDrop(files);
      },
    },
  };
}

/** Over the thread while a file is dragged across it. */
export function ThreadDropTarget() {
  return (
    <div className="ws-drop" aria-hidden="true">
      <div className="ws-drop-target">
        <FileUp className="ws-drop-icon" strokeWidth={1.6} />
        <strong>Drop to attach</strong>
        <span className="ws-drop-types">{WORKSHOP_ATTACH_TYPES}</span>
        <span className="ws-drop-where">{WORKSHOP_ATTACH_WHERE}</span>
      </div>
    </div>
  );
}
